import get from 'lodash.get'
import set from 'lodash/set'
import cloneDeep from 'lodash/cloneDeep'

/**
 * Paths in eCharts options where formatters are commonly used
 * Each entry specifies the path and what type of value it formats
 */
const FORMATTER_PATHS = [
  // Label formatters
  { path: 'label.formatter', type: 'value' },
  { path: 'endLabel.formatter', type: 'value' },
  { path: 'emphasis.label.formatter', type: 'value' },
  { path: 'blur.label.formatter', type: 'value' },
  { path: 'select.label.formatter', type: 'value' },

  // Tooltip formatters
  { path: 'tooltip.formatter', type: 'tooltip' },
  { path: 'tooltip.valueFormatter', type: 'value' },

  // AxisLabel formatters (though these are usually on axis components)
  { path: 'axisLabel.formatter', type: 'category' },

  // Animation label formatters
  { path: 'labelLayout.formatter', type: 'value' },

  // Mark line/point/area formatters
  { path: 'markPoint.label.formatter', type: 'value' },
  { path: 'markLine.label.formatter', type: 'value' },
  { path: 'markArea.label.formatter', type: 'value' }
]

/**
 * Creates a formatter function from a format string
 * @param {string} formatString - The format string (e.g., '0,0.00A')
 * @param {Function} formatter - The formatter function from FormattingContext
 * @param {string} type - The type of formatter ('value', 'category', 'tooltip')
 * @returns {Function} The formatter function for eCharts
 */
function createFormatterFunction (formatString, formatter, type) {
  if (type === 'tooltip') {
    // Tooltip formatters receive params object
    return (params) => {
      // Handle both single series and multiple series tooltips
      if (Array.isArray(params)) {
        return params.map(p => {
          const value = p.value?.[1] ?? p.value
          return `${p.seriesName}: ${formatter(value, formatString)}`
        }).join('<br/>')
      }
      const value = params.value?.[1] ?? params.value
      return formatter(value, formatString)
    }
  }

  if (type === 'value') {
    // Value formatters for labels
    return (params) => {
      // params can be a number or an object with value property
      const value = typeof params === 'number' ? params : (params.value?.[1] ?? params.value)
      return formatter(value, formatString)
    }
  }

  if (type === 'category') {
    // Category formatters
    return (value) => formatter(value, formatString)
  }

  // Default: simple formatter
  return (value) => formatter(value, formatString)
}

/**
 * Recursively processes props to replace format strings with formatter functions
 * @param {object} props - The props object to process
 * @param {Function} valueFormatter - The value formatter from context
 * @param {Function} categoryFormatter - The category formatter from context
 * @returns {object} Processed props with formatter functions
 */
export function interceptFormatters (props, valueFormatter, categoryFormatter) {
  // Clone props to avoid mutations
  const processedProps = cloneDeep(props)

  // Get the appropriate formatter based on type
  const getFormatter = (type) => {
    switch (type) {
      case 'category':
        return categoryFormatter
      case 'value':
      case 'tooltip':
      default:
        return valueFormatter
    }
  }

  // Check each formatter path
  FORMATTER_PATHS.forEach(({ path, type }) => {
    const value = get(processedProps, path)

    // If the value is a string, assume it's a format string
    if (typeof value === 'string') {
      const formatter = getFormatter(type)
      set(processedProps, path, createFormatterFunction(value, formatter, type))
    }
  })

  // Special handling for label.formatter with custom format
  if (props.label?.format && !props.label?.formatter) {
    const formatter = getFormatter('value')
    set(processedProps, 'label.formatter', createFormatterFunction(props.label.format, formatter, 'value'))
  }

  // Handle stackProps if it exists (for backward compatibility)
  if (props.stackProps?.label?.format) {
    const formatter = getFormatter('value')
    set(processedProps, 'stackProps.label.formatter', createFormatterFunction(props.stackProps.label.format, formatter, 'value'))
  }

  return processedProps
}

/**
 * Process series-specific format strings
 * @param {object} series - Series configuration object
 * @param {Function} valueFormatter - The value formatter from context
 * @param {Function} categoryFormatter - The category formatter from context
 * @returns {object} Processed series with formatter functions
 */
export function interceptSeriesFormatters (series, valueFormatter, categoryFormatter) {
  return interceptFormatters(series, valueFormatter, categoryFormatter)
}
